import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, Polyline } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useATMStore } from '../store/atmStore'
import type { ATMDevice } from '@atmvision/types'
import { useTheme } from '../hooks/useTheme'

// Module-level storm icon (avoid recreating on every render)
let _stormMarkerIcon: L.DivIcon | null = null
function getStormIcon(): L.DivIcon {
  if (!_stormMarkerIcon) {
    _stormMarkerIcon = L.divIcon({
      html: '<span style="font-size:32px;line-height:1;display:block;">🌀</span>',
      className: '',
      iconSize:   [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -18],
    })
  }
  return _stormMarkerIcon
}

const ZONE_CODE: Record<string, string> = {
  'Distrito Nacional': 'DN', 'Norte': 'NO', 'Sur': 'SU', 'Este': 'ES',
}
const ZONE_NAMES: Record<string, string> = {
  DN: 'Distrito Nacional', NO: 'Norte', SU: 'Sur', ES: 'Este',
}
const fmtNum = (n: number) => n.toLocaleString('es-DO')

// ── Seeded RNG (Mulberry32) — deterministic data every render ────────────────
function mulberry32(seed: number) {
  return function () {
    seed |= 0; seed = seed + 0x6D2B79F5 | 0
    let t = Math.imul(seed ^ seed >>> 15, 1 | seed)
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t
    return ((t ^ t >>> 14) >>> 0) / 4294967296
  }
}

// ── Types ────────────────────────────────────────────────────────────────────
interface HistoryDay {
  date: Date; dom: number; label: string
  uptime: number; incidents: number; tx: number; cashOut: number
  quincena: boolean; weekend: boolean; stormDay: boolean
}
interface ForecastDay {
  date: Date; dom: number; label: string; day: string
  predIncidents: number; predLo: number; predHi: number
  predUptime: number; confidence: 'high' | 'medium' | 'low'
  quincena: boolean; weekend: boolean
}
interface AtRiskDriver { icon: string; label: string; detail: string }
interface AtRiskEntry {
  atm: ATMDevice; zoneCode: string; locName: string
  score: number; hoursToEmpty: number
  drivers: AtRiskDriver[]; recAction: string
}
interface WeatherZone {
  temp: number; feels: number; humidity: number; wind: number; windDir: string
  cond: string; icon: string; precipChance: number; alertLevel: string
  risk: 'low' | 'medium' | 'high'; riskNote: string
  forecast: { day: string; icon: string; max: number; min: number; rain: number }[]
}
interface WeatherAlert {
  type: string; name: string; severity: string; zones: string[]
  windKph: number; rainMm: number; arrivalEta: string; duration: string
  impact: string; actions: string[]
}
interface ZoneAnomaly {
  zone: string; zoneName: string; total: number; oos: number; warn: number
  expectedOOS: number; sigma: number; avgCassettePct: number
  trend: 'up' | 'stable' | 'down'; topDriver: string
}
interface ModelRow {
  modelShort: string; modelFull: string; count: number
  faultRates: Record<string, number>; overall: number
}
interface RiskDay {
  date: Date; dayNum: number; label: string; dayName: string
  risk: 'high' | 'medium' | 'low'; tags: string[]
}

// ── Data builders ────────────────────────────────────────────────────────────

function buildHistory(): HistoryDay[] {
  const rng = mulberry32(20250501)
  const days = 30
  const out: HistoryDay[] = []
  const today = new Date()
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today.getTime() - i * 86400000)
    const dom = d.getDate()
    const quincena = dom === 14 || dom === 15 || dom === 29 || dom === 30 || dom === 1
    const weekend = d.getDay() === 0 || d.getDay() === 6
    const stormDay = i === 17 || i === 18
    const baseUptime = 99.2 + Math.sin(i * 0.3) * 0.3
    const uptime = stormDay ? baseUptime - 4.5
      : quincena ? baseUptime - 0.4
      : baseUptime + (rng() * 0.2 - 0.1)
    const incidents = stormDay ? 142
      : quincena ? 78 + Math.floor(rng() * 15)
      : weekend ? 28 + Math.floor(rng() * 10)
      : 42 + Math.floor(rng() * 16)
    const txMult = quincena ? 1.7 : weekend ? 0.6 : 1.0
    const tx = Math.floor((180000 + rng() * 22000) * txMult)
    const cashOut = Math.floor((85_000_000 + rng() * 22_000_000) * txMult)
    out.push({
      date: d, dom, label: `${dom}/${d.getMonth() + 1}`,
      uptime: Math.max(86, Math.min(99.9, uptime)),
      incidents, tx, cashOut, quincena, weekend, stormDay,
    })
  }
  return out
}

function buildForecast(): ForecastDay[] {
  const rng = mulberry32(20250502)
  const out: ForecastDay[] = []
  const today = new Date()
  for (let i = 1; i <= 7; i++) {
    const d = new Date(today.getTime() + i * 86400000)
    const dom = d.getDate()
    const quincena = dom === 14 || dom === 15 || dom === 29 || dom === 30
    const weekend = d.getDay() === 0 || d.getDay() === 6
    const base = quincena ? 84 : weekend ? 32 : 48
    out.push({
      date: d, dom, label: `${dom}/${d.getMonth() + 1}`,
      day: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'][d.getDay()],
      predIncidents: base, predLo: Math.max(15, base - 12), predHi: base + 18,
      predUptime: quincena ? 98.4 : weekend ? 99.6 : 99.3,
      confidence: i <= 3 ? 'high' : i <= 5 ? 'medium' : 'low',
      quincena, weekend,
    })
  }
  return out
}

// Deterministic hash from a string
function idHash(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

function buildAtRisk(fleet: ATMDevice[]): AtRiskEntry[] {
  if (fleet.length === 0) return []
  const rng = mulberry32(20250503)

  // Score every ATM deterministically, then take top 8
  const scored = fleet.map(a => {
    const zoneCode = ZONE_CODE[a.zone] ?? 'DN'
    const locName = a.municipality ? `${a.municipality}` : a.localidad

    const minCassPct = a.cashCassettes.length > 0
      ? Math.min(...a.cashCassettes.map(c => c.capacity > 0 ? (c.current / c.capacity) * 100 : 100))
      : 100

    // Base score from real status
    const statusBase = a.status === 'OUT_OF_SERVICE' ? 0.78
      : a.status === 'WARNING' ? 0.52
      : a.status === 'MAINTENANCE' ? 0.45
      : 0.15

    // Boost from low cash
    const cashBoost = minCassPct < 10 ? 0.25 : minCassPct < 20 ? 0.18 : minCassPct < 30 ? 0.10 : 0

    // Deterministic noise from terminal_id hash (so same ATM always gets same score)
    const noise = (idHash(a.terminal_id) % 1000) / 5000

    const score = Math.min(0.98, statusBase + cashBoost + noise + rng() * 0.08)

    const hoursToEmpty = Math.max(2, Math.min(96,
      minCassPct < 15 ? 4 + rng() * 8
      : minCassPct < 30 ? 14 + rng() * 12
      : 28 + rng() * 40
    ))

    const drivers: AtRiskDriver[] = []
    if (minCassPct < 25)
      drivers.push({ icon: '💵', label: 'Cash bajo', detail: `Cassette al ${Math.round(minCassPct)}%` })
    if (a.status === 'OUT_OF_SERVICE')
      drivers.push({ icon: '📉', label: 'Fuera de servicio', detail: 'Estado OOS' })
    else if (a.status === 'WARNING')
      drivers.push({ icon: '⚠️', label: 'Advertencia activa', detail: 'Estado WARNING' })
    const idcStatus = a.devices.find(d => d.component === 'IDC')?.status
    if (idcStatus === 'WARNING' || idcStatus === 'FAULT')
      drivers.push({ icon: '💳', label: 'Card reader', detail: `Estado ${idcStatus}` })
    const cdmStatus = a.devices.find(d => d.component === 'CDM')?.status
    if (cdmStatus === 'WARNING' || cdmStatus === 'FAULT')
      drivers.push({ icon: '🏧', label: 'Dispenser', detail: `Estado ${cdmStatus}` })
    if (a.zone === 'Sur' || a.zone === 'Este')
      drivers.push({ icon: '🌧️', label: 'Clima zona', detail: 'Lluvias previstas' })
    if (drivers.length === 0)
      drivers.push({ icon: '⚠️', label: 'Patrón histórico', detail: 'Falla típica miércoles' })

    return {
      atm: a, zoneCode, locName, score,
      hoursToEmpty,
      drivers: drivers.slice(0, 3),
      recAction: score > 0.7 ? 'Despachar técnico ahora'
        : minCassPct < 20 ? `Programar CIT en próximas ${Math.round(hoursToEmpty)}h`
        : 'Monitorear · revalidar en 6h',
    }
  })

  return scored.sort((a, b) => b.score - a.score).slice(0, 8)
}

// ── ML data builders (fleet-driven) ──────────────────────────────────────────

const COMPS = ['IDC', 'CDM', 'NET', 'UPS', 'PTR'] as const

function buildZoneAnomalies(fleet: ATMDevice[]): ZoneAnomaly[] {
  return (['DN', 'NO', 'SU', 'ES'] as const).map(zone => {
    const zAtms = fleet.filter(a => (ZONE_CODE[a.zone] ?? 'DN') === zone)
    const total = zAtms.length || 1
    const oos = zAtms.filter(a => a.status === 'OUT_OF_SERVICE').length
    const warn = zAtms.filter(a => a.status === 'WARNING').length
    const expectedOOS = Math.max(1, Math.round(total * 0.022))
    // σ: how many std-deviations above/below baseline (σ = √(n·p·(1-p)))
    const stdDev = Math.sqrt(total * 0.022 * 0.978) || 1
    const sigma = Math.round(((oos - expectedOOS) / stdDev) * 100) / 100
    const cassettePcts = zAtms.flatMap(a =>
      a.cashCassettes.map(c => c.capacity > 0 ? (c.current / c.capacity) * 100 : 100))
    const avgCassettePct = cassettePcts.length
      ? Math.round(cassettePcts.reduce((s, v) => s + v, 0) / cassettePcts.length)
      : 100
    // Top driver
    const topDriver =
      oos > expectedOOS * 1.5 ? `${oos} OOS — ${((oos / total) * 100).toFixed(1)}% de zona`
      : warn > total * 0.1 ? `${warn} en WARNING — ${((warn / total) * 100).toFixed(1)}%`
      : avgCassettePct < 30 ? `Cassette bajo (${avgCassettePct}%)`
      : 'Sin anomalías detectadas'
    return {
      zone, zoneName: ZONE_NAMES[zone] ?? zone, total, oos, warn,
      expectedOOS, sigma, avgCassettePct,
      trend: sigma > 0.6 ? 'up' : sigma < -0.4 ? 'down' : 'stable', topDriver,
    }
  })
}

function buildModelMatrix(fleet: ATMDevice[]): ModelRow[] {
  const map = new Map<string, ATMDevice[]>()
  for (const a of fleet) {
    const m = a.model || 'Unknown'
    if (!map.has(m)) map.set(m, [])
    map.get(m)!.push(a)
  }
  const rows: ModelRow[] = []
  for (const [model, atms] of map) {
    const n = atms.length
    const faultRates: Record<string, number> = {}
    for (const comp of COMPS) {
      const faults = atms.filter(a => {
        const d = a.devices.find(x => x.component === comp)
        return d?.status === 'FAULT' || d?.status === 'WARNING'
      }).length
      faultRates[comp] = n > 0 ? Math.round((faults / n) * 1000) / 1000 : 0
    }
    const overall = parseFloat((Object.values(faultRates).reduce((s, v) => s + v, 0) / COMPS.length).toFixed(4))
    const short = model.replace(/NCR\s+SelfServ\s*/i, 'SS-').replace(/Hyosung\s*/i, 'HS-').replace(/GRG\s*/i, 'GRG-')
    rows.push({ modelShort: short, modelFull: model, count: n, faultRates, overall })
  }
  return rows.sort((a, b) => b.overall - a.overall).slice(0, 6)
}

function buildRiskDays(): RiskDay[] {
  const today = new Date()
  return Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today.getTime() + i * 86400000)
    const dom = d.getDate()
    const dow = d.getDay()
    const quincena = [14, 15, 29, 30, 1].includes(dom)
    const weekend = dow === 0 || dow === 6
    const weather = i < 3
    const tags: string[] = []
    if (quincena) tags.push('Quincena')
    if (weather) tags.push('Onda tropical')
    if (weekend) tags.push('Fin semana')
    const risk: RiskDay['risk'] = quincena ? 'high' : (weather && !weekend) ? 'medium' : 'low'
    return {
      date: d, dayNum: dom, label: `${dom}/${d.getMonth() + 1}`,
      dayName: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'][dow],
      risk, tags,
    }
  })
}

// ── Static weather + alert ───────────────────────────────────────────────────

const WEATHER: Record<string, WeatherZone> = {
  DN: {
    temp: 31, feels: 36, humidity: 78, wind: 14, windDir: 'NE',
    cond: 'Parcialmente nublado', icon: '⛅', precipChance: 35, alertLevel: 'normal',
    risk: 'low', riskNote: 'Sin impacto operativo previsto',
    forecast: [
      { day: 'Hoy', icon: '⛅', max: 32, min: 24, rain: 35 },
      { day: 'Mié', icon: '🌦️', max: 30, min: 23, rain: 62 },
      { day: 'Jue', icon: '🌧️', max: 28, min: 22, rain: 85 },
      { day: 'Vie', icon: '⛅', max: 31, min: 24, rain: 40 },
      { day: 'Sáb', icon: '☀️', max: 33, min: 25, rain: 15 },
    ],
  },
  NO: {
    temp: 28, feels: 31, humidity: 72, wind: 11, windDir: 'NE',
    cond: 'Soleado con nubes', icon: '🌤️', precipChance: 20, alertLevel: 'normal',
    risk: 'low', riskNote: 'Operación nominal',
    forecast: [
      { day: 'Hoy', icon: '🌤️', max: 30, min: 21, rain: 20 },
      { day: 'Mié', icon: '⛅', max: 29, min: 22, rain: 35 },
      { day: 'Jue', icon: '🌦️', max: 28, min: 21, rain: 55 },
      { day: 'Vie', icon: '☀️', max: 31, min: 22, rain: 10 },
      { day: 'Sáb', icon: '☀️', max: 32, min: 23, rain: 5 },
    ],
  },
  SU: {
    temp: 33, feels: 38, humidity: 68, wind: 18, windDir: 'E',
    cond: 'Caluroso · ventoso', icon: '🌬️', precipChance: 15, alertLevel: 'normal',
    risk: 'low', riskNote: 'Temperaturas extremas — vigilar HVAC en cuartos técnicos',
    forecast: [
      { day: 'Hoy', icon: '☀️', max: 34, min: 24, rain: 15 },
      { day: 'Mié', icon: '☀️', max: 35, min: 25, rain: 10 },
      { day: 'Jue', icon: '⛅', max: 33, min: 24, rain: 25 },
      { day: 'Vie', icon: '🌦️', max: 31, min: 23, rain: 60 },
      { day: 'Sáb', icon: '🌧️', max: 29, min: 22, rain: 75 },
    ],
  },
  ES: {
    temp: 29, feels: 34, humidity: 84, wind: 28, windDir: 'NE',
    cond: 'Tormentas dispersas', icon: '⛈️', precipChance: 75, alertLevel: 'watch',
    risk: 'medium', riskNote: 'Onda tropical activa · ráfagas hasta 55km/h · riesgo conectividad WAN',
    forecast: [
      { day: 'Hoy', icon: '⛈️', max: 30, min: 23, rain: 75 },
      { day: 'Mié', icon: '🌧️', max: 28, min: 22, rain: 88 },
      { day: 'Jue', icon: '🌧️', max: 27, min: 22, rain: 92 },
      { day: 'Vie', icon: '⛅', max: 30, min: 23, rain: 35 },
      { day: 'Sáb', icon: '☀️', max: 32, min: 24, rain: 15 },
    ],
  },
}

const WEATHER_ALERT: WeatherAlert = {
  type: 'TROPICAL_WAVE', name: 'Onda tropical INVEST-92L',
  severity: 'watch', zones: ['ES', 'DN'],
  windKph: 55, rainMm: 80, arrivalEta: 'En curso', duration: '36–48h',
  impact: '≈ 240 ATMs en ruta de afectación · 14 sucursales costeras',
  actions: [
    'Pre-cargar cassettes en 32 ATMs zona Este',
    'Verificar UPS/batería en sucursales costeras',
    'CIT crews en standby · ETA 2h post-evento',
    'Comunicar canales digitales como alternativa',
  ],
}

// ── SVG chart helpers ─────────────────────────────────────────────────────────

interface AreaChartProps {
  data: HistoryDay[]; valueKey: 'uptime' | 'incidents'
  color?: string; fillOpacity?: number; yMin?: number; yMax?: number
  markers?: { label: string; text: string; color: string }[]
  w?: number; h?: number
}

function AreaChart({ data, valueKey, color = '#02AFF0', fillOpacity = 0.18, yMin, yMax, markers = [], w = 560, h = 140 }: AreaChartProps) {
  const C = useTheme()
  const vals = data.map(d => d[valueKey] as number)
  const lo = yMin != null ? yMin : Math.min(...vals)
  const hi = yMax != null ? yMax : Math.max(...vals)
  const padY = 16
  const xStep = (w - 36) / (data.length - 1)
  const yScale = (v: number) => padY + (h - padY * 2) * (1 - (v - lo) / (hi - lo || 1))
  const pts = data.map((d, i) => [30 + i * xStep, yScale(d[valueKey] as number)] as [number, number])
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L ${pts[pts.length - 1][0]} ${h - padY} L ${pts[0][0]} ${h - padY} Z`
  const gradId = `hv-g-${valueKey}`

  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={fillOpacity * 2} />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 1, 2, 3, 4].map(i => {
        const y = padY + (h - padY * 2) * (i / 4)
        const v = hi - (hi - lo) * (i / 4)
        return (
          <g key={i}>
            <line x1="30" y1={y} x2={w} y2={y} stroke="rgba(148,168,189,.15)" strokeDasharray="2 3" />
            <text x="4" y={y + 3} fontSize="9" fill="#94a8bd" fontFamily="JetBrains Mono">{v.toFixed(v < 100 ? 1 : 0)}</text>
          </g>
        )
      })}
      {markers.map((m, i) => {
        const xi = data.findIndex(d => d.label === m.label)
        if (xi < 0) return null
        const x = 30 + xi * xStep
        return (
          <g key={i}>
            <line x1={x} y1={padY} x2={x} y2={h - padY} stroke={m.color} strokeDasharray="3 3" strokeWidth="1.2" opacity="0.7" />
            <rect x={x - 22} y={2} width="44" height="14" rx="3" fill={m.color} />
            <text x={x} y={12} fontSize="8.5" fill="#fff" fontFamily="Open Sans" fontWeight="800" textAnchor="middle">{m.text}</text>
          </g>
        )
      })}
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]}
          r={data[i].stormDay ? 4 : data[i].quincena ? 3 : 2}
          fill={data[i].stormDay ? '#dc2626' : data[i].quincena ? C.orange : color}
          stroke="#fff" strokeWidth="1" />
      ))}
      {data.map((d, i) => i % 5 === 0 && (
        <text key={i} x={30 + i * xStep} y={h - 3} fontSize="8.5" fill="#94a8bd" fontFamily="JetBrains Mono" textAnchor="middle">{d.label}</text>
      ))}
    </svg>
  )
}

function ForecastChart({ data, w = 560, h = 155 }: { data: ForecastDay[]; w?: number; h?: number }) {
  const C = useTheme()
  const lo = 10, hi = 130, padY = 16
  const xStep = (w - 50) / (data.length - 1)
  const yScale = (v: number) => padY + (h - padY * 2 - 28) * (1 - (v - lo) / (hi - lo))
  const linePts = data.map((_, i) => [40 + i * xStep, yScale(data[i].predIncidents)] as [number, number])
  const hiPts = data.map((_, i) => [40 + i * xStep, yScale(data[i].predHi)] as [number, number])
  const loPts = data.map((_, i) => [40 + i * xStep, yScale(data[i].predLo)] as [number, number])
  const linePath = linePts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')
  const bandPath = hiPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')
    + ' ' + [...loPts].reverse().map(p => `L ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ') + ' Z'
  const chartH = h - 28

  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      {[0, 1, 2, 3, 4].map(i => {
        const y = padY + (chartH - padY * 2) * (i / 4)
        const v = hi - (hi - lo) * (i / 4)
        return (
          <g key={i}>
            <line x1="40" y1={y} x2={w} y2={y} stroke="rgba(148,168,189,.15)" strokeDasharray="2 3" />
            <text x="4" y={y + 3} fontSize="9" fill="#94a8bd" fontFamily="JetBrains Mono">{v.toFixed(0)}</text>
          </g>
        )
      })}
      <path d={bandPath} fill="rgba(168,85,247,.18)" stroke="rgba(168,85,247,.35)" strokeWidth="1" strokeDasharray="3 3" />
      <path d={linePath} fill="none" stroke="#a855f7" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      {linePts.map((p, i) => (
        <g key={i}>
          <circle cx={p[0]} cy={p[1]} r={data[i].quincena ? 5 : 3.2}
            fill={data[i].quincena ? C.orange : '#a855f7'} stroke="#fff" strokeWidth="1.5" />
          <text x={p[0]} y={chartH + 6} fontSize="10" fill={C.text} fontFamily="Open Sans" fontWeight="700" textAnchor="middle">
            {data[i].day}
          </text>
          <text x={p[0]} y={chartH + 18} fontSize="8.5" fill="#94a8bd" fontFamily="JetBrains Mono" textAnchor="middle">
            {data[i].label}
          </text>
        </g>
      ))}
    </svg>
  )
}

// ── ML Metrics Strip ─────────────────────────────────────────────────────────

function MLMetricsStrip() {
  const C = useTheme()
  const metrics = [
    { label: 'Precisión modelo',  value: '91.4%', detail: 'P@5 (últimos 30d)',        col: C.green  },
    { label: 'Recall',            value: '87.2%', detail: 'incidentes P1+P2',          col: C.green  },
    { label: 'RMSE diario',       value: '4.3',   detail: 'incidentes · intervalo 95%',col: C.blue   },
    { label: 'F1 Score',          value: '0.892', detail: 'spike quincena',            col: C.blue   },
    { label: 'Actualizado hace',  value: '2h',    detail: 'ciclo re-train cada 6h',    col: C.purple },
    { label: 'Training set',      value: '893d',  detail: '× 933 ATMs',               col: C.muted  },
  ]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 10 }}>
      {metrics.map((m, i) => (
        <div key={i} style={{
          background: C.card, borderRadius: 9, border: `1px solid ${C.border}`,
          padding: '10px 12px', borderTop: `3px solid ${m.col}`,
        }}>
          <div style={{ fontSize: 9, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{m.label}</div>
          <div style={{ fontSize: 20, fontWeight: 800, fontFamily: C.mono, color: m.col, lineHeight: 1 }}>{m.value}</div>
          <div style={{ fontSize: 9.5, color: C.muted, marginTop: 3, fontWeight: 600 }}>{m.detail}</div>
        </div>
      ))}
    </div>
  )
}

// ── Zone Anomaly Detection ────────────────────────────────────────────────────

function ZoneAnomalyGrid({ data }: { data: ZoneAnomaly[] }) {
  const C = useTheme()
  const zoneCol: Record<string, string> = { DN: C.blue, NO: C.green, SU: C.orange, ES: C.purple }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1, background: C.border }}>
      {data.map(a => {
        const col = zoneCol[a.zone] ?? C.blue
        const sigmaColor = Math.abs(a.sigma) > 1 ? C.red : Math.abs(a.sigma) > 0.5 ? C.orange : C.green
        const trendIcon = a.trend === 'up' ? '↑' : a.trend === 'down' ? '↓' : '→'
        const trendCol  = a.trend === 'up' ? C.red : a.trend === 'down' ? C.green : C.muted
        return (
          <div key={a.zone} style={{ background: C.card2, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: col, flexShrink: 0 }} />
              <span style={{ fontSize: 10.5, fontWeight: 800, color: C.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>{a.zoneName}</span>
              <span style={{ marginLeft: 'auto', fontSize: 16, fontWeight: 800, color: trendCol }}>{trendIcon}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
              <StatCell label="OOS real"       value={String(a.oos)}  hiColor={a.oos > a.expectedOOS ? C.red : C.green} />
              <StatCell label="OOS esperado"   value={String(a.expectedOOS)} />
              <StatCell label="En WARNING"     value={String(a.warn)} hiColor={a.warn > a.total * 0.05 ? C.orange : undefined} />
              <StatCell label="Cassette avg"   value={`${a.avgCassettePct}%`} hiColor={a.avgCassettePct < 30 ? C.red : a.avgCassettePct < 50 ? C.orange : C.green} />
            </div>
            {/* Sigma bar */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: C.muted, fontWeight: 700, marginBottom: 3 }}>
                <span>Anomalía (σ)</span>
                <span style={{ color: sigmaColor, fontFamily: C.mono }}>{a.sigma > 0 ? '+' : ''}{a.sigma.toFixed(2)}σ</span>
              </div>
              <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(100, Math.abs(a.sigma) * 40)}%`, background: sigmaColor }} />
              </div>
            </div>
            <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4, fontStyle: 'italic' }}>{a.topDriver}</div>
          </div>
        )
      })}
    </div>
  )
}

// ── Model Reliability Heatmap ─────────────────────────────────────────────────

function ModelReliabilityHeatmap({ rows }: { rows: ModelRow[] }) {
  const C = useTheme()
  const rateCol = (r: number) => r > 0.08 ? C.red : r > 0.04 ? C.orange : r > 0.01 ? '#facc15' : C.green
  const rateBg  = (r: number) => r > 0.08 ? 'rgba(239,68,68,.15)' : r > 0.04 ? 'rgba(248,148,33,.12)' : r > 0.01 ? 'rgba(250,204,21,.10)' : 'rgba(34,197,94,.07)'
  const cols = `150px repeat(${COMPS.length},1fr) 90px`

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 4, marginBottom: 4 }}>
        <div style={{ fontSize: 9.5, color: C.muted, fontWeight: 800, padding: '4px 8px' }}>MODELO</div>
        {COMPS.map(c => <div key={c} style={{ fontSize: 9.5, color: C.muted, fontWeight: 800, textAlign: 'center', padding: '4px 2px' }}>{c}</div>)}
        <div style={{ fontSize: 9.5, color: C.muted, fontWeight: 800, textAlign: 'center', padding: '4px 2px' }}>TOTAL</div>
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: cols, gap: 4, marginBottom: 4, alignItems: 'center' }}>
          <div style={{ padding: '6px 8px' }}>
            <div style={{ fontFamily: C.mono, fontWeight: 800, fontSize: 11, color: C.blue }}>{row.modelShort}</div>
            <div style={{ fontSize: 8.5, color: C.muted }}>{row.count} uds</div>
          </div>
          {COMPS.map(comp => {
            const r = row.faultRates[comp] ?? 0
            return (
              <div key={comp} style={{ textAlign: 'center', padding: '7px 2px', borderRadius: 6, background: rateBg(r), border: `1px solid ${rateCol(r)}22` }}>
                <span style={{ fontFamily: C.mono, fontWeight: 800, fontSize: 12, color: rateCol(r) }}>
                  {r > 0 ? `${(r * 100).toFixed(1)}%` : '—'}
                </span>
              </div>
            )
          })}
          <div style={{ textAlign: 'center', padding: '7px 2px', borderRadius: 6, background: rateBg(row.overall), border: `1px solid ${rateCol(row.overall)}33` }}>
            <span style={{ fontFamily: C.mono, fontWeight: 800, fontSize: 13, color: rateCol(row.overall) }}>
              {(row.overall * 100).toFixed(1)}%
            </span>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 10, color: C.muted, fontWeight: 700 }}>
        {[{ c: C.green, l: '< 1%  Normal' }, { c: '#facc15', l: '1–4%  Vigilancia' }, { c: C.orange, l: '4–8%  Degradado' }, { c: C.red, l: '> 8%  Crítico' }]
          .map(({ c, l }) => (
            <span key={l}><span style={{ display: 'inline-block', width: 10, height: 10, background: c, borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />{l}</span>
          ))}
      </div>
    </div>
  )
}

// ── 14-day Risk Calendar ──────────────────────────────────────────────────────

function RiskCalendarGrid({ days }: { days: RiskDay[] }) {
  const C = useTheme()
  const rc: Record<string, string>  = { high: C.red, medium: C.orange, low: C.green }
  const rb: Record<string, string>  = { high: 'rgba(239,68,68,.14)', medium: 'rgba(248,148,33,.11)', low: 'rgba(34,197,94,.07)' }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 7 }}>
      {days.map((d, i) => (
        <div key={i} style={{
          padding: '8px 6px', borderRadius: 8, textAlign: 'center',
          background: rb[d.risk], border: `1px solid ${rc[d.risk]}44`,
        }}>
          <div style={{ fontSize: 9, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{d.dayName}</div>
          <div style={{ fontFamily: C.mono, fontSize: 17, fontWeight: 800, color: rc[d.risk], margin: '3px 0 2px' }}>{d.dayNum}</div>
          <div style={{ fontSize: 8.5, color: C.muted }}>{d.date.toLocaleDateString('es-DO', { month: 'short' })}</div>
          {d.tags.length > 0 && (
            <div style={{ marginTop: 5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {d.tags.map((t, j) => (
                <div key={j} style={{ fontSize: 8, fontWeight: 700, color: rc[d.risk], background: rb[d.risk], borderRadius: 4, padding: '1px 3px', lineHeight: 1.3 }}>{t}</div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Card wrapper ──────────────────────────────────────────────────────────────
function Card({ title, sub, children, noPad = false }: { title: React.ReactNode; sub?: React.ReactNode; children: React.ReactNode; noPad?: boolean }) {
  const C = useTheme()
  return (
    <div style={{ background: C.card, borderRadius: 11, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: C.card2,
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{title}</span>
        {sub && <span style={{ fontSize: 10.5, color: C.muted, fontWeight: 600 }}>{sub}</span>}
      </div>
      <div style={noPad ? {} : { padding: '14px 12px' }}>{children}</div>
    </div>
  )
}

function Dot({ color }: { color: string }) {
  const C = useTheme()
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 4 }} />
}

// ── Weather Modal ─────────────────────────────────────────────────────────────

// Approximate storm track for INVEST-92L (east → west across northern Caribbean)
const STORM_TRACK: [number, number][] = [
  [17.2, -60.8],  // Atlantic origin
  [17.8, -64.2],  // Near USVI
  [18.3, -67.5],  // Mona Passage
  [18.6, -69.3],  // DR Este (current)
  [18.9, -71.4],  // DR Central
  [19.3, -74.0],  // Haiti border
  [19.8, -76.8],  // Projected west
]

// Current storm eye is at index 3 (DR Este coast)
const STORM_EYE: [number, number] = [18.6, -69.3]

// Zone center coordinates (DR)
const ZONE_CENTERS: Record<string, [number, number]> = {
  ES: [18.70, -68.60],
  DN: [18.48, -69.93],
  NO: [19.45, -70.69],
  SU: [18.03, -70.55],
}

const ZONE_RISK_COLOR: Record<string, string> = {
  ES: '#ef4444',    // high — direct hit
  DN: '#F89421',    // medium — watch
  NO: '#22c55e',    // low — safe
  SU: '#22c55e',    // low — safe
}

const ZONE_ATM_COUNT: Record<string, number> = { ES: 182, DN: 312, NO: 241, SU: 198 }

function WeatherModal({ onClose }: { onClose: () => void }) {
  const C = useTheme()

  const header: React.CSSProperties = {
    background: 'linear-gradient(135deg,#7c2d12,#b45309)',
    padding: '16px 20px',
    display: 'flex', alignItems: 'center', gap: 16,
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    flexShrink: 0,
  }
  const pill = (color: string): React.CSSProperties => ({
    display: 'inline-block',
    background: `${color}22`, border: `1px solid ${color}55`,
    color, borderRadius: 5, padding: '2px 9px',
    fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
  })

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(8,16,26,0.88)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.card, borderRadius: 16, border: '1px solid rgba(255,255,255,0.1)',
          width: '100%', maxWidth: 1080, maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 28px 90px rgba(0,0,0,0.75)', overflow: 'hidden',
        }}
      >
        {/* ── Modal header ──────────────────────────────────────────── */}
        <div style={header}>
          <div style={{ fontSize: 40, flexShrink: 0, lineHeight: 1 }}>🌀</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: 1.8, marginBottom: 3 }}>
              ⚠ ALERTA METEOROLÓGICA ACTIVA — CATEGORÍA WATCH
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 3 }}>
              {WEATHER_ALERT.name} · Zonas {WEATHER_ALERT.zones.join(' + ')}
            </div>
            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.8)' }}>
              {WEATHER_ALERT.impact} &nbsp;·&nbsp; Estado: <strong style={{ color: '#fbbf24' }}>En curso</strong>
            </div>
          </div>
          {/* Stats */}
          <div style={{ display: 'flex', gap: 24, fontFamily: C.mono, flexShrink: 0, marginRight: 12 }}>
            {[
              { val: WEATHER_ALERT.windKph, unit: 'km/h', lbl: 'Ráfagas' },
              { val: WEATHER_ALERT.rainMm,  unit: 'mm',   lbl: 'Lluvia 24h' },
              { val: WEATHER_ALERT.duration, unit: '',    lbl: 'Duración' },
            ].map(({ val, unit, lbl }) => (
              <div key={lbl} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
                  {val}<small style={{ fontSize: 11, opacity: 0.7, marginLeft: 2 }}>{unit}</small>
                </div>
                <div style={{ fontSize: 9, color: '#fbbf24', letterSpacing: 0.5, textTransform: 'uppercase', fontWeight: 700 }}>{lbl}</div>
              </div>
            ))}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', borderRadius: 8, width: 38, height: 38, cursor: 'pointer', fontSize: 18, fontFamily: 'inherit', flexShrink: 0, lineHeight: 1 }}
          >✕</button>
        </div>

        {/* ── Modal body ────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'grid', gridTemplateColumns: '1fr 380px', minHeight: 0 }}>

          {/* Left: Leaflet weather map */}
          <div style={{ position: 'relative', overflow: 'hidden' }}>
            <MapContainer
              center={[18.7, -70.3]}
              zoom={7}
              style={{ height: '100%', width: '100%', minHeight: 480 }}
              zoomControl
              scrollWheelZoom={false}
              attributionControl={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Storm track — dashed red polyline */}
              <Polyline
                positions={STORM_TRACK}
                pathOptions={{ color: '#ef4444', weight: 2.5, dashArray: '8 5', opacity: 0.65 }}
              />
              {/* Predicted track (lighter, after eye) */}
              <Polyline
                positions={STORM_TRACK.slice(3)}
                pathOptions={{ color: '#f97316', weight: 2, dashArray: '4 6', opacity: 0.4 }}
              />

              {/* Storm eye marker */}
              <Marker position={STORM_EYE} icon={getStormIcon()}>
                <Popup>
                  <div style={{ fontFamily: 'sans-serif', fontSize: 12, minWidth: 160 }}>
                    <strong>INVEST-92L — Posición actual</strong><br />
                    🌡️ Vientos: {WEATHER_ALERT.windKph} km/h<br />
                    💧 Lluvia prevista: {WEATHER_ALERT.rainMm} mm/24h<br />
                    ⏱ Duración estimada: {WEATHER_ALERT.duration}
                  </div>
                </Popup>
              </Marker>

              {/* Zone circles — colored by risk */}
              {Object.entries(ZONE_CENTERS).map(([code, pos]) => {
                const w = WEATHER[code]
                const color = ZONE_RISK_COLOR[code]
                const radius = code === 'ES' ? 32 : code === 'DN' ? 26 : 20
                return (
                  <CircleMarker
                    key={code}
                    center={pos}
                    radius={radius}
                    pathOptions={{ color, fillColor: color, fillOpacity: code === 'ES' ? 0.22 : 0.12, weight: 2.5, dashArray: code === 'SU' || code === 'NO' ? undefined : '6 3' }}
                  >
                    <Popup>
                      <div style={{ fontFamily: 'sans-serif', fontSize: 12, minWidth: 170, lineHeight: 1.5 }}>
                        <strong style={{ color: ZONE_RISK_COLOR[code] }}>{ZONE_NAMES[code]}</strong>
                        &nbsp;— {ZONE_ATM_COUNT[code]} ATMs<br />
                        {w.icon} {w.cond}<br />
                        🌡️ {w.temp}°C (sensación {w.feels}°C)<br />
                        💧 Prob. lluvia: {w.precipChance}%<br />
                        💨 Viento: {w.wind} km/h {w.windDir}<br />
                        ⚠️ {w.riskNote}
                      </div>
                    </Popup>
                  </CircleMarker>
                )
              })}
            </MapContainer>

            {/* Map legend overlay */}
            <div style={{
              position: 'absolute', bottom: 14, left: 14, zIndex: 1000,
              background: 'rgba(15,20,26,0.92)', borderRadius: 8, padding: '10px 14px',
              border: '1px solid rgba(255,255,255,0.12)', fontSize: 10.5, color: '#e7ecf3',
              pointerEvents: 'none',
            }}>
              <div style={{ fontWeight: 800, marginBottom: 6, fontSize: 9.5, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a8bd' }}>Riesgo operativo ATM</div>
              {[
                { c: '#ef4444', l: 'Alto — Alerta activa' },
                { c: '#F89421', l: 'Medio — Vigilancia (Watch)' },
                { c: '#22c55e', l: 'Bajo — Operación normal' },
              ].map(({ c, l }) => (
                <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                  <span style={{ width: 11, height: 11, borderRadius: '50%', background: c, display: 'inline-block', border: `2px solid ${c}`, flexShrink: 0 }} />
                  {l}
                </div>
              ))}
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 7 }}>
                <svg width={22} height={6} style={{ flexShrink: 0 }}>
                  <line x1={0} y1={3} x2={22} y2={3} stroke="#ef4444" strokeWidth={2} strokeDasharray="6 4" />
                </svg>
                Trayectoria INVEST-92L
              </div>
            </div>

            {/* ONAMET source badge */}
            <div style={{
              position: 'absolute', top: 10, left: 10, zIndex: 1000,
              background: 'rgba(15,20,26,0.85)', borderRadius: 6, padding: '4px 10px',
              border: '1px solid rgba(255,255,255,0.1)', fontSize: 9.5, color: '#94a8bd',
              pointerEvents: 'none',
            }}>
              📡 ONAMET · NHC NOAA · actualizado hace 12 min
            </div>
          </div>

          {/* Right: Details panel */}
          <div style={{ overflowY: 'auto', padding: '18px 20px', borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Classification */}
            <div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Clasificación</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[
                  { l: 'Tipo', v: 'Onda Tropical', c: '#F89421' },
                  { l: 'Categoría', v: 'WATCH', c: '#ef4444' },
                  { l: 'Fuente', v: 'ONAMET + NHC', c: C.blue },
                  { l: 'ETD', v: WEATHER_ALERT.arrivalEta, c: C.muted },
                ].map(({ l, v, c }) => (
                  <div key={l} style={{ background: C.tint, borderRadius: 7, padding: '7px 10px' }}>
                    <div style={{ fontSize: 8.5, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{l}</div>
                    <div style={{ fontWeight: 800, color: c, fontSize: 11, marginTop: 2 }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ATM impact by zone */}
            <div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Impacto por Zona</div>
              {(['ES', 'DN', 'NO', 'SU'] as const).map(code => {
                const w = WEATHER[code]
                const color = ZONE_RISK_COLOR[code]
                const riskLabel = w.risk === 'high' ? 'ALERTA' : w.risk === 'medium' ? 'WATCH' : 'NORMAL'
                return (
                  <div key={code} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
                    borderBottom: `1px solid ${C.border}`,
                  }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: C.text }}>{ZONE_NAMES[code]}</div>
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{ZONE_ATM_COUNT[code]} ATMs · {w.cond}</div>
                    </div>
                    <span style={pill(color)}>{riskLabel}</span>
                  </div>
                )
              })}
            </div>

            {/* 5-day forecast — zona Este (most affected) */}
            <div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
                Pronóstico Zona Este (más afectada)
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {WEATHER['ES'].forecast.map((f, i) => (
                  <div key={i} style={{ flex: 1, textAlign: 'center', background: C.tint, borderRadius: 8, padding: '8px 4px' }}>
                    <div style={{ fontSize: 9, color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>{f.day}</div>
                    <div style={{ fontSize: 20, margin: '4px 0' }}>{f.icon}</div>
                    <div style={{ fontSize: 9.5, fontFamily: C.mono, fontWeight: 800, color: C.text }}>{f.max}°</div>
                    <div style={{ fontSize: 8.5, color: f.rain > 60 ? '#60a5fa' : C.muted, fontWeight: 700 }}>{f.rain}%</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick stats */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
              {[
                { l: 'ATMs afectados', v: '~240', c: C.orange },
                { l: 'Sucursales costeras', v: '14', c: C.red },
                { l: 'ATMs en riesgo OOS', v: '~32', c: C.red },
              ].map(({ l, v, c }) => (
                <div key={l} style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, fontFamily: C.mono, color: c, lineHeight: 1 }}>{v}</div>
                  <div style={{ fontSize: 8.5, color: C.muted, marginTop: 3, fontWeight: 600, lineHeight: 1.3 }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Recommended actions */}
            <div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
                Acciones Recomendadas
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {WEATHER_ALERT.actions.map((a, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)',
                    borderRadius: 8, padding: '8px 11px', fontSize: 11, color: C.text, lineHeight: 1.4,
                  }}>
                    <span style={{ color: '#22c55e', fontSize: 13, flexShrink: 0, marginTop: 1 }}>✓</span>
                    {a}
                  </div>
                ))}
              </div>
            </div>

            {/* Correlation risk note */}
            <div style={{
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 9, padding: '10px 12px', fontSize: 10.5, color: '#fca5a5', lineHeight: 1.6,
            }}>
              <strong style={{ color: '#ef4444' }}>⚠ Riesgo combinado:</strong> Marea de quincena + lluvia intensa es la combinación con mayor factor multiplicador de incidentes (×4.2). Priorizar reposición preventiva antes del evento.
            </div>

          </div>
        </div>

        {/* ── Modal footer ──────────────────────────────────────────── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 20px', borderTop: `1px solid ${C.border}`,
          background: C.card2, flexShrink: 0, fontSize: 10, color: C.muted,
        }}>
          <span>📡 Fuente: ONAMET · NHC NOAA · Integración ATMVISION · Próxima actualización en 15 min</span>
          <button
            onClick={onClose}
            style={{
              background: C.tint, border: `1px solid ${C.border}`, color: C.text,
              borderRadius: 7, padding: '6px 18px', cursor: 'pointer', fontSize: 11,
              fontFamily: 'inherit', fontWeight: 600,
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ── Main view ─────────────────────────────────────────────────────────────────
export default function HistoricoView() {
  const C = useTheme()
  const { fleet } = useATMStore()
  const history        = useMemo(() => buildHistory(), [])
  const forecast       = useMemo(() => buildForecast(), [])
  const atRisk         = useMemo(() => buildAtRisk(fleet), [fleet])
  const zoneAnomalies  = useMemo(() => buildZoneAnomalies(fleet), [fleet])
  const modelMatrix    = useMemo(() => buildModelMatrix(fleet), [fleet])
  const riskDays       = useMemo(() => buildRiskDays(), [])

  const [showWeatherModal, setShowWeatherModal] = useState(false)

  const totalIncidents = history.reduce((s, d) => s + d.incidents, 0)
  const avgUptime = history.reduce((s, d) => s + d.uptime, 0) / history.length
  const stormMarker = history[12] ? [{ label: history[12].label, text: 'Tormenta', color: '#dc2626' }] : []
  const quincenaLabel = forecast.find(f => f.quincena)?.label ?? '—'

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 14, background: C.dark, display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* ── Header ── */}
      <div style={{ background: C.card, borderRadius: 11, border: `1px solid ${C.border}`, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 10, flexShrink: 0,
          background: 'linear-gradient(135deg,#a855f7,#6b35a0)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
        }}>📊</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>Histórico, predicción &amp; clima</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>
            30 días de telemetría + forecast 7 días + correlación meteorológica. Modelo: gradient-boost sobre features de fleet × calendario × clima zona.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 20, flexShrink: 0 }}>
          {([
            { val: '30d', sub: 'Histórico', col: C.blue },
            { val: '7d', sub: 'Forecast', col: C.purple },
            { val: '87%', sub: 'Precisión 7d', col: C.green },
          ] as const).map(({ val, sub, col }) => (
            <div key={sub} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800, fontFamily: C.mono, color: col }}>{val}</div>
              <div style={{ fontSize: 9, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── ML model metrics strip ── */}
      <MLMetricsStrip />

      {/* ── Weather alert banner (click to open full modal) ── */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setShowWeatherModal(true)}
        onKeyDown={(e) => e.key === 'Enter' && setShowWeatherModal(true)}
        style={{
          background: 'linear-gradient(135deg,#7c2d12,#b45309)', color: '#fff',
          borderRadius: 11, padding: '14px 18px', position: 'relative', overflow: 'hidden',
          border: '1px solid rgba(248,148,33,.4)',
          cursor: 'pointer',
          transition: 'filter .15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.filter = 'brightness(1.08)')}
        onMouseLeave={e => (e.currentTarget.style.filter = '')}
      >
        <div style={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, background: 'radial-gradient(circle,rgba(248,148,33,.3) 0%,transparent 65%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
          <div style={{ fontSize: 34, flexShrink: 0, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,.4))' }}>🌀</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 3 }}>
              ⚠ Alerta meteorológica activa — <span style={{ fontWeight: 500, opacity: 0.85 }}>click para ver mapa y detalles →</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>
              {WEATHER_ALERT.name} · zonas {WEATHER_ALERT.zones.join(' + ')}
            </div>
            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.85)', lineHeight: 1.5 }}>{WEATHER_ALERT.impact}</div>
          </div>
          <div style={{ display: 'flex', gap: 20, fontFamily: C.mono, flexShrink: 0 }}>
            {[
              { val: WEATHER_ALERT.windKph, unit: 'km/h', lbl: 'Ráfagas' },
              { val: WEATHER_ALERT.rainMm, unit: 'mm', lbl: 'Lluvia 24h' },
              { val: WEATHER_ALERT.duration, unit: '', lbl: 'Duración' },
            ].map(({ val, unit, lbl }) => (
              <div key={lbl} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, whiteSpace: 'nowrap' }}>
                  {val}<small style={{ fontSize: 10, opacity: 0.7, marginLeft: 2 }}>{unit}</small>
                </div>
                <div style={{ fontSize: 9, opacity: 0.7, letterSpacing: 0.5, textTransform: 'uppercase' }}>{lbl}</div>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', fontSize: 22, opacity: 0.7, paddingLeft: 6 }}>›</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.15)' }}>
          {WEATHER_ALERT.actions.map((a, i) => (
            <div key={i} style={{ background: 'rgba(0,0,0,.25)', padding: '5px 11px', borderRadius: 14, fontSize: 10.5, fontWeight: 600 }}>
              ✓ {a}
            </div>
          ))}
        </div>
      </div>

      {/* ── Weather modal ── */}
      {showWeatherModal && <WeatherModal onClose={() => setShowWeatherModal(false)} />}

      {/* ── Charts row: uptime + incidents ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card title="📈 Uptime fleet · 30 días" sub={`media: ${avgUptime.toFixed(2)}%`}>
          <AreaChart data={history} valueKey="uptime" yMin={86} yMax={100} color={C.green} markers={stormMarker} />
          <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 10.5, color: C.muted, fontWeight: 600 }}>
            <span><Dot color="#dc2626" />Tormenta tropical</span>
            <span><Dot color={C.orange} />Quincena</span>
            <span style={{ marginLeft: 'auto', fontFamily: C.mono, color: C.green }}>SLA target ≥ 99.0%</span>
          </div>
        </Card>
        <Card title="⚠️ Incidentes diarios · 30 días" sub={`total: ${fmtNum(totalIncidents)}`}>
          <AreaChart data={history} valueKey="incidents" yMin={0} yMax={150} color={C.orange} markers={stormMarker} />
          <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 10.5, color: C.muted, fontWeight: 600 }}>
            <span style={{ fontFamily: C.mono }}>p50: 47/d · p95: 88/d</span>
            <span style={{ marginLeft: 'auto', fontFamily: C.mono, color: C.orange }}>spike quincenal: ×1.7</span>
          </div>
        </Card>
      </div>

      {/* ── Zone anomaly detection ── */}
      <Card title="🔬 Anomaly Detection · zonas en tiempo real" sub="σ vs baseline histórico 30d · actualizado cada 60s" noPad>
        <ZoneAnomalyGrid data={zoneAnomalies} />
      </Card>

      {/* ── Forecast + At-risk ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card title="🔮 Forecast incidentes · 7 días" sub="banda 80% confianza">
          <ForecastChart data={forecast} />
          <div style={{
            background: 'rgba(168,85,247,.1)', border: '1px solid rgba(168,85,247,.3)',
            borderRadius: 8, padding: '9px 12px', marginTop: 10, fontSize: 11, color: '#c4b5fd', lineHeight: 1.5,
          }}>
            <strong>Insight:</strong> spike previsto para <strong>{quincenaLabel}</strong> (quincena). Pre-cargar cassettes RD$1000 y RD$2000 en zonas DN+NO antes de las 06:00.
          </div>
        </Card>
        <Card title="🎯 ATMs en riesgo · próximas 24h" sub={`${atRisk.length} en lista`} noPad>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {atRisk.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: C.muted, fontSize: 12 }}>
                Sin ATMs en riesgo alto en este momento
              </div>
            ) : atRisk.map(r => (
              <div key={r.atm.terminal_id} style={{
                display: 'grid', gridTemplateColumns: '50px 1fr auto',
                gap: 10, padding: '10px 14px',
                borderBottom: `1px solid ${C.border}`, alignItems: 'center',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: 20, fontWeight: 800, fontFamily: C.mono, lineHeight: 1,
                    color: r.score > 0.7 ? C.red : r.score > 0.5 ? C.orange : C.blue,
                  }}>
                    {Math.round(r.score * 100)}
                  </div>
                  <div style={{ fontSize: 8, color: C.muted, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>riesgo</div>
                </div>
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: C.text }}>
                    <span style={{ fontFamily: C.mono, color: C.blue, fontWeight: 800, marginRight: 6 }}>{r.atm.terminal_id}</span>
                    {r.locName} · {r.zoneCode}
                  </div>
                  <div style={{ display: 'flex', gap: 5, marginTop: 4, flexWrap: 'wrap' }}>
                    {r.drivers.map((d, j) => (
                      <span key={j} style={{
                        fontSize: 9.5, background: C.tint, padding: '2px 7px',
                        borderRadius: 9, color: C.muted, fontWeight: 600,
                      }}>
                        {d.icon} {d.label}
                      </span>
                    ))}
                  </div>
                  <div style={{ fontSize: 10, color: C.blue, marginTop: 3, fontWeight: 600 }}>→ {r.recAction}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{
                    fontFamily: C.mono, fontSize: 13, fontWeight: 800,
                    color: r.hoursToEmpty < 12 ? C.red : r.hoursToEmpty < 24 ? C.orange : C.green,
                  }}>
                    {Math.round(r.hoursToEmpty)}h
                  </div>
                  <div style={{ fontSize: 8.5, color: C.muted, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>a vacío</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── Weather per zone ── */}
      <Card title="🌤️ Clima por zona × correlación operativa" sub="datos: ONAMET · refresh cada 15 min" noPad>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1, background: C.border }}>
          {Object.entries(WEATHER).map(([code, w]) => {
            const riskColor = w.risk === 'high' ? C.red : w.risk === 'medium' ? C.orange : C.green
            return (
              <div key={code} style={{ background: C.card2, padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 32, lineHeight: 1 }}>{w.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 9.5, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {ZONE_NAMES[code]}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, fontFamily: C.mono, color: C.text, lineHeight: 1.1 }}>
                      {w.temp}°<span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>C</span>
                    </div>
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>sensación {w.feels}°</div>
                  </div>
                </div>
                <div style={{ fontSize: 11.5, color: C.text, fontWeight: 700, marginBottom: 10 }}>{w.cond}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 10.5, marginBottom: 10 }}>
                  <StatCell label="Lluvia" value={`${w.precipChance}%`} hiColor={w.precipChance > 60 ? '#60a5fa' : undefined} />
                  <StatCell label="Viento" value={`${w.wind} ${w.windDir}`} />
                  <StatCell label="Humedad" value={`${w.humidity}%`} />
                  <StatCell label="Riesgo ATM" value={w.risk.toUpperCase()} hiColor={riskColor} border={riskColor} />
                </div>
                <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4, fontStyle: 'italic', marginBottom: 10 }}>{w.riskNote}</div>
                <div style={{ display: 'flex', gap: 4, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                  {w.forecast.map((f, i) => (
                    <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{ fontSize: 9, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{f.day}</div>
                      <div style={{ fontSize: 18, margin: '2px 0' }}>{f.icon}</div>
                      <div style={{ fontSize: 9.5, fontFamily: C.mono, fontWeight: 800, color: C.text }}>
                        {f.max}°<span style={{ color: C.muted, fontWeight: 600 }}>/{f.min}°</span>
                      </div>
                      <div style={{ fontSize: 8.5, color: f.rain > 60 ? '#60a5fa' : C.muted, fontFamily: C.mono, fontWeight: 700 }}>{f.rain}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </Card>

      {/* ── Model reliability heatmap ── */}
      <Card title="⚙️ Confiabilidad por modelo × componente" sub="tasa de fallos activos · fleet en vivo">
        {modelMatrix.length > 0
          ? <ModelReliabilityHeatmap rows={modelMatrix} />
          : <div style={{ color: '#94a8bd', fontSize: 12, textAlign: 'center', padding: 24 }}>Sin datos de flota disponibles</div>}
      </Card>

      {/* ── 14-day operational risk calendar ── */}
      <Card title="📅 Calendario de riesgo operativo · próximos 14 días" sub="modelo predictivo: quincena × clima × calendario">
        <RiskCalendarGrid days={riskDays} />
        <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 10.5, fontWeight: 700 }}>
          {[{ c: '#ef4444', l: 'Alto · pre-cargar cassettes + standby CIT' }, { c: '#F89421', l: 'Medio · monitoreo reforzado' }, { c: '#22c55e', l: 'Bajo · operación normal' }]
            .map(({ c, l }) => (
              <span key={l} style={{ color: c }}><span style={{ display: 'inline-block', width: 10, height: 10, background: c, borderRadius: 2, marginRight: 5, verticalAlign: 'middle' }} />{l}</span>
            ))}
        </div>
      </Card>

      {/* ── Correlation matrix ── */}
      <Card title="🔗 Correlación clima ↔ incidentes (últimos 30d)" sub="factor de impacto sobre baseline">
        {([
          ['Lluvia intensa (>50mm/24h)', 'NET_DOWN, ISO_TIMEOUT', 2.4, 'Saturación enlaces WAN microondas, especial zona Este'],
          ['Tormenta eléctrica', 'PWR_FAIL, SEC_TAMPER', 3.1, 'Picos de tensión disparan UPS y sensores'],
          ['Calor extremo (>34°C)', 'DSP_DEGRADED, PTR_JAM', 1.6, 'HVAC sobrecargado en cuartos técnicos costeros'],
          ['Viento fuerte (>50km/h)', 'NET_DOWN', 1.9, 'Antenas microondas pierden alineación'],
          ['Marea de quincena × lluvia', 'CDM_CASH_LOW (cluster)', 4.2, 'Combinación más nociva: tx alta + acceso CIT bloqueado'],
        ] as [string, string, number, string][]).map(([cond, codes, mult, note], i) => (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '1fr 180px 110px 1fr',
            gap: 14, padding: '10px 0',
            borderBottom: i < 4 ? `1px solid ${C.border}` : 'none',
            alignItems: 'center',
          }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: C.text }}>{cond}</div>
            <div>
              {codes.split(',').map((c, j) => (
                <span key={j} style={{
                  display: 'inline-block', background: C.tint, fontFamily: C.mono,
                  fontSize: 10, padding: '2px 7px', borderRadius: 5, marginRight: 4, marginBottom: 2,
                  color: C.blue, fontWeight: 700,
                }}>{c.trim()}</span>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, height: 8, background: C.border, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${Math.min(100, mult * 22)}%`,
                  background: mult > 3 ? C.red : mult > 2 ? C.orange : C.blue,
                }} />
              </div>
              <span style={{
                fontFamily: C.mono, fontWeight: 800, fontSize: 12,
                color: mult > 3 ? C.red : mult > 2 ? C.orange : C.blue,
              }}>×{mult}</span>
            </div>
            <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.4 }}>{note}</div>
          </div>
        ))}
      </Card>

    </div>
  )
}

function StatCell({ label, value, hiColor, border }: { label: string; value: string; hiColor?: string; border?: string }) {
  const C = useTheme()
  return (
    <div style={{
      background: C.tint, padding: '5px 8px', borderRadius: 6,
      ...(border ? { borderLeft: `3px solid ${border}` } : {}),
    }}>
      <div style={{ fontSize: 8.5, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontFamily: C.mono, fontWeight: 800, color: hiColor ?? C.text }}>{value}</div>
    </div>
  )
}
